import React, { useMemo, useState, useCallback } from 'react';
import { DataGridPremium, GridToolbarContainer, useGridApiRef, GridCellParams, GridColDef, GridColumnGroupingModel, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { Box, IconButton, Snackbar, Alert, Tooltip, LinearProgress } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import axiosInstance from "../AxiosInstance";
import {isMoneyGreaterThanZeroThreeDecimals} from "../Utilities/validation";
import {isForecastWeekInPast} from "../Utilities/forecastWeekEditability";
import { formatWeekHeader } from "../Utilities/weekNumberUtils";
import { 
    formatCurrency, 
    safeParseFloat, 
    updateWeeklyDistributionRates, 
    getChangedUnitCosts,
    WeeklyDistributionRate,
    SkuBaseForecastRow
} from './UnitCostForecastTable.utils';
import ResetDialog from './shared/ResetDialog';
import OverrideIndicatorCell from './shared/OverrideIndicatorCell';
import './SkuBaseForecastTable.css';

// Error types for better error handling
interface ApiError {
    response?: {
        data?: string;
    };
    message?: string;
}


interface PromotionProduct {
    skuBaseForecastId: string;
    weeklyDistributionRates?: string[];
}

interface SnackbarState {
    children: string;
    severity: 'success' | 'error';
}

interface EditedRow {
    original: SkuBaseForecastRow;
    updated: SkuBaseForecastRow;
}

interface UnitCostOverride {
    skuBaseForecastId: string;
    weekStart: string;
    unitCost: number | null;
}

interface UnitCostForecastTableProps {
    forecastId: string;
    forecastPeriodId: string;
    isLocked: boolean;
    promotionProductsData?: PromotionProduct[];
    allowPastWeeks?: boolean;
    rows: SkuBaseForecastRow[];
    onReload: () => Promise<void>;
}


const UnitCostForecastTable: React.FC<UnitCostForecastTableProps> = ({
    forecastId,
    forecastPeriodId,
    isLocked, 
    promotionProductsData,
    allowPastWeeks = false,
    rows,
    onReload
}) => {
    const apiRef = useGridApiRef();
    const [editedRows, setEditedRows] = useState<Record<string, EditedRow>>({});
    const [unsavedCellChanges, setUnsavedCellChanges] = useState<Set<string>>(new Set());
    const [snackbar, setSnackbar] = useState<SnackbarState | null>(null);
    const [resetDialog, setResetDialog] = useState<{
        open: boolean;
        rowId: string;
        week: string;
        currentValue: number;
        baseValue: number;
    }>({
        open: false,
        rowId: '',
        week: '',
        currentValue: 0,
        baseValue: 0
    });

    const handleCloseSnackbar = (): void => setSnackbar(null);

    const handleSaveClick = async (): Promise<void> => {
        try {
            const overrides: UnitCostOverride[] = Object.values(editedRows).flatMap(({ original, updated }) => {
                return getChangedUnitCosts(
                    updated.weeklyDistributionRates,
                    original.weeklyDistributionRates
                ).map(change => {
                    // Find the updated week data to check if override was removed
                    const updatedWeek = updated.weeklyDistributionRates.find(w => w.week === change.weekStart);
                    const originalWeek = original.weeklyDistributionRates.find(w => w.week === change.weekStart);
                    
                    // If the override flag changed from true to false, send null to delete the override
                    const wasOverridden = originalWeek?.hasUnitCostOverride || false;
                    const isOverridden = updatedWeek?.hasUnitCostOverride || false;
                    
                    return {
                        skuBaseForecastId: updated.id,
                        weekStart: change.weekStart,
                        unitCost: (wasOverridden && !isOverridden) ? null : change.unitCost
                    };
                });
            });

            const payload = {
                unitCostOverrides: overrides,
                forecastPeriodId
            };

            if (payload.unitCostOverrides.length === 0) return;

            await axiosInstance.post(`/forecasts/${forecastId}/skubaseforecasts/unitcosts/bulk`, payload);

            setSnackbar({ children: 'Unit Costs updated successfully.', severity: 'success' });
            setEditedRows({});
            setUnsavedCellChanges(new Set());
            await onReload();
        } catch (error: unknown) {
            const apiError = error as ApiError;
            const errorMessage = apiError.response?.data || apiError.message || 'Error saving unit cost changes.';
            setSnackbar({ children: errorMessage, severity: 'error' });
        }
    };

    const CustomToolbar: React.FC = () => (
        <GridToolbarContainer sx={{ justifyContent: 'flex-end' }}>
            <Tooltip title="Save">
        <span>
          <IconButton
              aria-label="save"
              onClick={handleSaveClick}
              disabled={Object.keys(editedRows).length === 0}
          >
            <SaveIcon />
          </IconButton>
        </span>
            </Tooltip>
        </GridToolbarContainer>
    );

    const processRowUpdate = (newRow: SkuBaseForecastRow, oldRow: SkuBaseForecastRow): SkuBaseForecastRow => {
        const validationErrors: string[] = [];
        let updatedRow: SkuBaseForecastRow = { ...newRow };

        if (!updatedRow.weeklyDistributionRates) return oldRow;

        // Process dynamic week fields and update weekly distribution rates
        Object.keys(updatedRow).forEach((key) => {
            if (key !== 'id' && key !== 'skuDescription' && key !== 'weeklyDistributionRates') {
                const value = (updatedRow as any)[key];
                if (typeof value === 'string' || typeof value === 'number') {
                    updatedRow = updateWeeklyDistributionRates(updatedRow, key, value.toString(), 'unitCost');
                    // Remove the dynamic property after processing
                    delete (updatedRow as any)[key];
                }
            }
        });

        const changedUnitCosts = getChangedUnitCosts(updatedRow.weeklyDistributionRates, oldRow.weeklyDistributionRates);

        // Validate and reset invalid values
        for (const change of changedUnitCosts) {
            if (change.unitCost !== null && !isMoneyGreaterThanZeroThreeDecimals(change.unitCost)) {
                validationErrors.push(`Invalid Unit Cost for week ${change.weekStart}`);
                const originalWeek = oldRow.weeklyDistributionRates.find(w => w.week === change.weekStart);
                if (originalWeek) {
                    updatedRow = updateWeeklyDistributionRates(updatedRow, change.weekStart, originalWeek.unitCost?.toString() || '0', 'unitCost');
                }
            }
        }

        if (validationErrors.length > 0) {
            setSnackbar({ children: validationErrors.join('\n'), severity: 'error' });
        }

        // Always update editedRows when a row is processed, even if no changes
        setEditedRows(prev => ({
            ...prev,
            [updatedRow.id]: {
                original: oldRow,
                updated: updatedRow,
            }
        }));

        // Track unsaved cell changes
        if (changedUnitCosts.length > 0) {
            setUnsavedCellChanges(prev => {
                const newSet = new Set(prev);
                changedUnitCosts.forEach(change => {
                    newSet.add(`${updatedRow.id}-${change.weekStart}`);
                });
                return newSet;
            });
        }

        return updatedRow;
    };

    const handleProcessRowUpdateError = useCallback((error: Error) => {
        setSnackbar({ children: error.message, severity: 'error' });
    }, []);

    const handleResetDialogClose = () => {
        setResetDialog({ ...resetDialog, open: false });
    };

    const handleResetConfirm = () => {
        const { rowId, week, baseValue } = resetDialog;
        
        // Get the current row (edited or original)
        const editedRow = editedRows[rowId];
        const row = editedRow?.updated || editedRow?.original || rows.find(r => r.id === rowId);
        
        if (!row) return;

        // Update the unit cost to the base value for this week
        const updatedWeeklyRates = row.weeklyDistributionRates.map(w => 
            w.week === week 
                ? { ...w, unitCost: baseValue, hasUnitCostOverride: false }
                : w
        );
        
        const updatedRow: SkuBaseForecastRow = {
            ...row,
            weeklyDistributionRates: updatedWeeklyRates
        };

        // Get original row for comparison
        const originalRow = editedRow?.original || row;

        // Update edited rows state
        setEditedRows(prev => ({
            ...prev,
            [rowId]: {
                original: originalRow,
                updated: updatedRow,
            }
        }));

        // Track this cell as having unsaved changes
        setUnsavedCellChanges(prev => new Set(prev).add(`${rowId}-${week}`));

        // Update grid display
        apiRef.current.updateRows([updatedRow]);

        setResetDialog({ ...resetDialog, open: false });
    };

    const isCellEditable = (params: GridCellParams): boolean => {
        const { field } = params;

        return !(isForecastWeekInPast(field, allowPastWeeks) || isLocked);
    };

    const { columns, columnGroupingModel } = useMemo((): { columns: GridColDef[]; columnGroupingModel: GridColumnGroupingModel } => {
        const monthMap = new Map<string, { field: string }[]>();
        
        const weeklyColumns: GridColDef[] = rows.length && rows[0]?.weeklyDistributionRates
            ? rows[0].weeklyDistributionRates.map((wdr, index) => {
                const customMonth = wdr.forecastWeek.month;
                const monthName = new Date(2000, customMonth - 1, 1)
                    .toLocaleString('default', { month: 'short' })
                    .toUpperCase();

                const column: GridColDef = {
                    field: wdr.week,
                    headerName: formatWeekHeader(wdr.week, index),
                    type: 'number',
                    width: 100,
                    editable: true,
                    valueGetter: (_value: unknown, row: SkuBaseForecastRow) => 
                        row.weeklyDistributionRates.find(w => w.week === wdr.week)?.unitCost,
                    cellClassName: (params: GridCellParams) => {
                        const classes: string[] = [];
                        const isPastOrLocked = isForecastWeekInPast(params.field, allowPastWeeks) || isLocked;
                        const isHighlighted = promotionProductsData?.some(pp =>
                            pp.skuBaseForecastId === params.id &&
                            pp.weeklyDistributionRates?.includes(params.field)
                        );

                        if (isHighlighted) classes.push('highlightedCell');
                        if (isPastOrLocked) classes.push('nonEditableCell');

                        return classes.join(' ');
                    },
                    valueFormatter: (value: number | undefined) => formatCurrency(value),
                    renderCell: (params: GridRenderCellParams) => {
                        const weekData = params.row.weeklyDistributionRates.find((w: WeeklyDistributionRate) => w.week === params.field);
                        const hasOverride = weekData?.hasUnitCostOverride || false;
                        const value = weekData?.unitCost || 0;
                        const isPastOrLocked = isForecastWeekInPast(params.field, allowPastWeeks) || isLocked;
                        
                        const currentWeekIndex = params.row.weeklyDistributionRates.findIndex((w: WeeklyDistributionRate) => w.week === params.field);
                        const baseValue = currentWeekIndex > 0
                            ? params.row.weeklyDistributionRates[currentWeekIndex - 1].unitCost || 0
                            : 0;
                        
                        const cellKey = `${params.id}-${params.field}`;
                        const hasUnsavedChanges = unsavedCellChanges.has(cellKey);
                        
                        const handleDotClick = () => {
                            setResetDialog({
                                open: true,
                                rowId: params.id as string,
                                week: params.field,
                                currentValue: value,
                                baseValue: baseValue
                            });
                        };
                        
                        return (
                            <OverrideIndicatorCell
                                value={formatCurrency(value)}
                                hasOverride={hasOverride}
                                isPastOrLocked={isPastOrLocked}
                                hasUnsavedChanges={hasUnsavedChanges}
                                onDotClick={handleDotClick}
                            />
                        );
                    }
                };

                if (!monthMap.has(monthName)) {
                    monthMap.set(monthName, []);
                }
                monthMap.get(monthName)!.push({ field: wdr.week });

                return column;
            })
            : [];

        const columnGroupingModel: GridColumnGroupingModel = Array.from(monthMap.entries()).map(([groupId, children]) => ({
            groupId,
            headerName: groupId,
            children
        }));

        const baseColumns: GridColDef[] = [
            { field: 'id', headerName: 'ID', width: 90 },
            { field: 'skuDescription', headerName: 'SKU', width: 300 }
        ];

        return {
            columns: [...baseColumns, ...weeklyColumns],
            columnGroupingModel
        };
    }, [rows, isLocked, promotionProductsData, unsavedCellChanges]);

    return (
        <Box sx={{ height: '100%', width: '100%' }}>
            <DataGridPremium
                apiRef={apiRef}
                editMode="row"
                rows={rows}
                columns={columns}
                columnGroupingModel={columnGroupingModel}
                experimentalFeatures={{ columnGrouping: true } as any}
                isCellEditable={isCellEditable}
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={handleProcessRowUpdateError}
                slots={{ toolbar: CustomToolbar, loadingOverlay: LinearProgress as any }}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            id: false,
                        },
                    },
                    pinnedColumns: { left: ['skuDescription'] }
                }}
                cellSelection
                disableClipboardPaste
            />
            {!!snackbar && (
                <Snackbar
                    open
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    onClose={handleCloseSnackbar}
                    autoHideDuration={6000}
                >
                    <Alert {...snackbar} onClose={handleCloseSnackbar} />
                </Snackbar>
            )}
            <ResetDialog
                open={resetDialog.open}
                currentValue={resetDialog.currentValue}
                baseValue={resetDialog.baseValue}
                onClose={handleResetDialogClose}
                onConfirm={handleResetConfirm}
            />
        </Box>
    );
};

export default UnitCostForecastTable;
